import { useCallback, useContext } from 'react';

import { GrowthBookContext } from '@growthbook/growthbook-react';

import { FlowConsumerContextEnergy } from '../../context';
import { getOffers } from '../helpers';
import { Offer, OfferResponse } from '../types';

interface ExperimentConfig {
  experimentKey: string;
  filterPredicate: (offer: Offer, variant: string | null) => boolean;
}

interface FilterOffersParams {
  context: FlowConsumerContextEnergy;
  offer?: OfferResponse;
  compareFn?: (a: Offer, b: Offer) => number;
}
const DEFAULT_FILTER_PREDICATE = () => true;
export const useGetOffers = (experimentConfig?: ExperimentConfig) => {
  const experimentKey = experimentConfig?.experimentKey || '';
  // Read the GrowthBook context directly instead of useFeatureValue: the latter throws
  // "Missing or invalid GrowthBookProvider" when no provider is present. Flows that opt out of
  // experiments (e.g. the contract extension flow) render this hook without a GrowthBookProvider.
  const { growthbook } = useContext(GrowthBookContext);
  const variant = experimentKey && growthbook ? growthbook.getFeatureValue(experimentKey, null) : null;
  const filterPredicate = experimentConfig?.filterPredicate || DEFAULT_FILTER_PREDICATE;

  const filterOffers = useCallback(
    ({ context, offer, compareFn }: FilterOffersParams) => {
      let offers = getOffers(context, offer);
      if (compareFn) {
        offers = offers.sort(compareFn);
      }

      if (!experimentKey) {
        return offers;
      }

      return offers.filter(offer => filterPredicate(offer, variant));
    },
    [experimentKey, filterPredicate, variant],
  );

  return { filterOffers };
};
